import {
	Button,
	DataTable,
	type DataTableColumn,
	EmptyState,
	Input,
	PageHeader,
	Stack,
	Surface,
	Text,
	Toolbar,
} from "@app/ui"
import { type ShipmentStatus, StatusBadge, Tabs } from "@app/ui/next"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Link, createRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import {
	type ShipmentSummary,
	exportShipmentsCsv,
	listShipments,
	shipmentsQueryKeys,
} from "../../api/shipments"
import { downloadTextFile, scopedExportFilename } from "../../lib/download"
import { formatDate, humanizeStatus, shipmentRef } from "../../lib/format"
import { HttpError } from "../../lib/http"
import { useScope } from "../../scope/scope-context"
import { Route as RootRoute } from "../__root"

/**
 * Shipments list -- `/shipments`.
 *
 * Code-based route (matches the existing scaffold pattern in
 * `routes/index.tsx`). Once the TanStack Router file-based plugin
 * lands in vite.config and `routeTree.gen.ts` is wired through
 * `router.tsx`, this can become `createFileRoute("/shipments/")`.
 */
export const Route = createRoute({
	getParentRoute: () => RootRoute,
	path: "/shipments",
	component: ShipmentsIndexPage,
})

const COMPANY_LINE =
	"Latest 100 shipments for your tenant, newest first. Search by booking, BL, container number, or shipment ID."

// Engine status (opaque string) → RAM-OS logistics status, for StatusBadge colour.
// Unknown values fall back to `pending` (neutral tint); the real engine label is shown
// verbatim via StatusBadge's `label`, so a status is never miscalled.
const STATUS_MAP: Record<string, ShipmentStatus> = {
	DELIVERED: "delivered",
	ARRIVED: "delivered",
	COMPLETED: "delivered",
	IN_TRANSIT: "transit",
	BOOKED: "pending",
	PLANNED: "pending",
	DELAYED: "hold",
	ON_HOLD: "hold",
	CANCELLED: "exception",
	EXCEPTION: "exception",
}
const toShipmentStatus = (status: string): ShipmentStatus =>
	STATUS_MAP[status.toUpperCase()] ?? "pending"

// View-filter tabs → the ShipmentStatus they select ("all" shows everything).
const VIEWS = [
	{ id: "all", label: "All" },
	{ id: "transit", label: "In transit" },
	{ id: "hold", label: "On hold" },
	{ id: "delivered", label: "Delivered" },
	{ id: "exception", label: "Exceptions" },
] as const
type View = (typeof VIEWS)[number]["id"]

const formatLane = (shipment: ShipmentSummary): string => {
	const origin = shipment.originUnlocode ?? "—"
	const destination = shipment.destinationUnlocode ?? "—"
	return `${origin} → ${destination}`
}

export function ShipmentsIndexPage() {
	const navigate = useNavigate()
	const { params: scope } = useScope()
	const [q, setQ] = useState("")
	const trimmedQ = q.trim()
	const query = useQuery({
		queryKey: shipmentsQueryKeys.list(trimmedQ, scope),
		queryFn: ({ signal }) =>
			listShipments({ ...(trimmedQ.length > 0 ? { q: trimmedQ } : {}), scope, signal }),
	})

	// Export pulls the same filters the list is showing, so "Export CSV" means
	// "export what I'm looking at" rather than "export everything".
	const exportMutation = useMutation({
		mutationFn: async () => {
			const csv = await exportShipmentsCsv({
				...(trimmedQ.length > 0 ? { q: trimmedQ } : {}),
				scope,
			})
			downloadTextFile(csv, scopedExportFilename("shipments", scope, new Date()))
		},
	})

	const [view, setView] = useState<View>("all")
	const allRows = query.data ?? []
	// View filter is client-side over the fetched (q-filtered) page; tab counts
	// reflect that same set so they stay consistent with the search.
	const rows = view === "all" ? allRows : allRows.filter((s) => toShipmentStatus(s.status) === view)
	const tabs = VIEWS.map((v) => ({
		id: v.id,
		label: v.label,
		count:
			v.id === "all"
				? allRows.length
				: allRows.filter((s) => toShipmentStatus(s.status) === v.id).length,
	}))
	const countLabel = query.isSuccess
		? `${rows.length} ${rows.length === 1 ? "shipment" : "shipments"}`
		: "Shipments"

	const columns: DataTableColumn<ShipmentSummary>[] = [
		{
			key: "ref",
			header: "Booking / Ref",
			mono: true,
			cell: (s) => (
				// A native link inside the row: keeps cmd/middle-click + prefetch +
				// keyboard focus while the whole row also navigates via onRowClick.
				<Link
					to="/shipments/$shipmentId"
					params={{ shipmentId: s.id }}
					onClick={(e) => e.stopPropagation()}
				>
					{shipmentRef(s)}
				</Link>
			),
		},
		{ key: "bl", header: "BL", mono: true, cell: (s) => s.blNumber ?? "—" },
		{ key: "lane", header: "Route", cell: (s) => formatLane(s) },
		{ key: "carrier", header: "Carrier", cell: (s) => s.carrierName ?? "—" },
		{ key: "eta", header: "ETA", mono: true, align: "end", cell: (s) => formatDate(s.eta) },
		{
			key: "status",
			header: "Status",
			align: "end",
			cell: (s) => (
				<StatusBadge status={toShipmentStatus(s.status)} label={humanizeStatus(s.status)} />
			),
		},
	]

	return (
		<Stack gap="5" align="stretch">
			<PageHeader
				title="Shipments"
				subtitle={COMPANY_LINE}
				actions={
					<Stack direction="row" gap="3">
						<Button
							variant="secondary"
							size="sm"
							disabled={exportMutation.isPending || allRows.length === 0}
							onClick={() => exportMutation.mutate()}
						>
							{exportMutation.isPending ? "Exporting…" : "Export CSV"}
						</Button>
						<Button
							variant="primary"
							size="sm"
							onClick={() => void navigate({ to: "/shipments/new" })}
						>
							New shipment
						</Button>
					</Stack>
				}
			/>

			<Tabs tabs={tabs} value={view} onChange={(id) => setView(id as View)} />

			<Toolbar lead={<Text variant="label">{countLabel}</Text>}>
				<Input
					type="search"
					inputSize="sm"
					placeholder="Search booking / BL / container / id…"
					value={q}
					onChange={(e) => setQ(e.target.value)}
				/>
				{q.length > 0 ? (
					<Button variant="secondary" size="sm" onClick={() => setQ("")}>
						Clear
					</Button>
				) : null}
			</Toolbar>

			{query.isError ? (
				<ErrorState error={query.error} onRetry={() => query.refetch()} />
			) : (
				<DataTable
					columns={columns}
					rows={rows}
					getRowKey={(s) => s.id}
					isLoading={query.isPending}
					onRowClick={(s) =>
						navigate({ to: "/shipments/$shipmentId", params: { shipmentId: s.id } })
					}
					empty={<EmptyStateBody hasQuery={trimmedQ.length > 0} filtered={view !== "all"} />}
				/>
			)}
		</Stack>
	)
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
	const message =
		error instanceof HttpError
			? `HTTP ${error.status}`
			: error instanceof Error
				? error.message
				: "Unknown error"
	return (
		<Surface elevation="1" tone="muted" bordered>
			<Stack gap="3" align="start">
				<Text variant="heading-sm" tone="danger">
					Couldn't load shipments
				</Text>
				<Text variant="body-sm" tone="muted">
					{message}
				</Text>
				<Button variant="secondary" size="sm" onClick={onRetry}>
					Retry
				</Button>
			</Stack>
		</Surface>
	)
}

function EmptyStateBody({ hasQuery, filtered }: { hasQuery: boolean; filtered: boolean }) {
	const title = hasQuery ? "No matches" : filtered ? "Nothing in this view" : "No shipments yet"
	const description = hasQuery
		? "Try a different booking number, BL, or shipment ID."
		: filtered
			? "No shipments currently match this status filter."
			: "Bookings created in the engine will appear here."
	return <EmptyState title={title} description={description} />
}
